import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AppToaster } from '@meno/ui/components/AppToaster'
import { AuthProvider, useAuth } from '@meno/ui/contexts/AuthContext'
import { FrameLoader } from '@meno/ui/components/FrameLoader'
import { setAppLoader } from '@meno/ui/components/AppLoader'
import { ThemeProvider } from '@meno/ui/contexts/ThemeContext'
import { LanguageProvider } from '@meno/ui/contexts/LanguageContext'
import { ScreenStatusProvider } from '@meno/ui/contexts/ScreenStatusContext'
import AppShell from '@/components/layout/AppShell'
import { TaskCenterProvider } from '@/contexts/TaskCenterContext'
import { UploadProvider } from '@/contexts/UploadContext'
import RequireAuth from '@meno/ui/components/RequireAuth'
import RequireOrgEntitlement from '@meno/ui/components/RequireOrgEntitlement'
import RequireAppAccess from '@meno/ui/components/RequireAppAccess'
import RequirePermission from '@meno/ui/components/RequirePermission'
import LoginPage    from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import AccessDeniedPage from '@meno/ui/pages/AccessDeniedPage'
import DashboardPage        from '@/pages/DashboardPage'
import ScreensPage          from '@/pages/ScreensPage'
import ScreenBuilderPage    from '@/pages/ScreenBuilderPage'
import ScreenDetailsPage   from '@/pages/ScreenDetailsPage'
import MediaPage            from '@/pages/management/MediaPage'
import ContentHubPage       from '@/pages/ContentHubPage'
import TabletPage            from '@/pages/TabletPage'
import AudioHubPage         from '@/pages/AudioHubPage'
import ActivityCenterPage  from '@/pages/ActivityCenterPage'
import TransitionsPage      from '@/pages/TransitionsPage'
import AudioPlaylistDetailsPage from '@/pages/AudioPlaylistDetailsPage'
import UsersPage            from '@/pages/management/UsersPage'
import NotFoundPage          from '@/pages/NotFoundPage'

import BrandAssetsPage      from '@/pages/management/BrandAssetsPage'
import TutorialsPage        from '@/pages/TutorialsPage'
import SettingsPage         from '@/pages/SettingsPage'
import PlaylistDetailsPage  from '@/pages/management/PlaylistDetailsPage'
import LayoutDetailsPage    from '@/pages/management/LayoutDetailsPage'
import MixDetailsPage       from '@/pages/management/MixDetailsPage'
import StopScreenDetailsPage from '@/pages/management/StopScreenDetailsPage'

function WeTookLoadingScreen() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background">
      <FrameLoader />
    </div>
  )
}



/** Redirects already-logged-in users away from auth pages. */
function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <WeTookLoadingScreen />
  if (user) return <Navigate to="/overview" replace />
  // In production AUTH_URL is set; local auth pages are unreachable in prod.
  return <>{children}</>
}

// Frame shows its own mark while it waits. Declared here rather than passed to
// each gate: RequireAuth / RequireAppAccess / RequirePermission live in
// @meno/ui and are shared with every app, so entering Frame went through three
// of them and every one drew the house "W".
setAppLoader(FrameLoader)

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <LanguageProvider>
       <ScreenStatusProvider>
       <TaskCenterProvider>
        <UploadProvider>
        <AppToaster />
        <BrowserRouter>
          <Routes>

            {/* ── Public auth pages ─────────────────────────── */}
            <Route path="/login"         element={<GuestOnly><LoginPage /></GuestOnly>} />
            <Route path="/register"      element={<GuestOnly><RegisterPage /></GuestOnly>} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />

            {/* ── Protected app shell ───────────────────────── */}
            <Route element={<RequireAuth><RequireOrgEntitlement appKind="signage" appLabel="Frame"><RequireAppAccess appKind="signage" appLabel="Frame"><AppShell /></RequireAppAccess></RequireOrgEntitlement></RequireAuth>}>
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="/overview"                element={<DashboardPage />} />
              <Route path="/transitions"             element={<TransitionsPage />} />
              <Route path="/activity"                element={<ActivityCenterPage />} />
              <Route path="/content-hub"             element={<ContentHubPage />} />
              <Route path="/tablet"                  element={<TabletPage />} />
              <Route path="/audio-hub"               element={<AudioHubPage />} />
              <Route path="/audio-hub/playlists/:id" element={<AudioPlaylistDetailsPage />} />
              <Route path="/screens"                 element={<ScreensPage />} />
              <Route path="/screens/new"             element={<RequirePermission permission="screens:create" redirectTo="/screens"><ScreenBuilderPage /></RequirePermission>} />
              <Route path="/screens/:id"             element={<ScreenDetailsPage />} />
              {/* Visual menus were dropped; old links land on Media → Links. */}
              <Route path="/menus"                   element={<Navigate to="/management/media#links" replace />} />
              <Route path="/menus/:id"               element={<Navigate to="/management/media#links" replace />} />
              <Route path="/management/users"        element={<UsersPage />} />

              <Route path="/management/brand-assets"   element={<BrandAssetsPage />} />
              <Route path="/management/media"        element={<MediaPage />} />
              <Route path="/management/media/playlists/:id" element={<PlaylistDetailsPage />} />
              <Route path="/management/mixes/:id"    element={<MixDetailsPage />} />
              <Route path="/management/layouts/:id"  element={<LayoutDetailsPage />} />
              <Route path="/management/stop-screens/:id" element={<StopScreenDetailsPage />} />
              <Route path="/tutorials"               element={<TutorialsPage />} />
              <Route path="/settings"                element={<SettingsPage />} />
            </Route>

            {/* ── 404 / Fallback ────────────────────────────── */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </UploadProvider>
       </TaskCenterProvider>
       </ScreenStatusProvider>
      </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}
